import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { RefreshCw, Download, Activity, AlertTriangle, TrendingDown } from 'lucide-react'
import {
  ResponsiveContainer, ComposedChart, BarChart, LineChart, Bar, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts'
import { useTranslation } from 'react-i18next'
import { useScope } from '../contexts/ScopeContext'
import { useTheme } from '../contexts/ThemeContext'
import { ScopeChip } from './MineScopeSelect'
import DateRangePicker from './DateRangePicker'
import i18n from '../i18n'
import { fmtNum as fmtNumIntl, fmtInt as fmtIntIntl, fmtPct as fmtPctIntl, fmtDayShort } from '../i18n/format'

// Paleta categórica validada (ordem FIXA, cor segue a entidade):
// realizado/produção/ore = teal, plano/meta = azul, waste = âmbar, 4ª série = violeta.
const C_REAL = '#0d9488'
const C_PLAN = '#1570ef'
const C_WASTE = '#d97706'
const C_FOURTH = '#9333ea'
// Cores de status GMG (sempre com rótulo visível, nunca só cor).
const G_PROD = '#17b26a'
const G_STANDBY = '#f79009'
const G_DOWN = '#f04438'
const G_DELAY = '#92400e'
const G_NONPROD = '#dd2590'
const G_IDLE = '#94a3b8'

const TARGETS = { df: 85, uf: 75, ro: 65 }

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)
const fmt = (v) => fmtNumIntl(num(v))
const fmtInt = (v) => fmtIntIntl(num(v))
// O /api/kpis devolve os indicadores em PONTOS percentuais (85 = 85%); o Intl com
// style:'percent' espera fração, então divide antes, senão sai 8.500,0%.
const fmtPct = (pts) => fmtPctIntl(num(pts) / 100, 1)

function fmtHours(secs) {
  return `${fmt(num(secs) / 3600)} ${i18n.t('common:units.hours')}`
}

function fmtMMSS(s) {
  s = Math.max(0, Math.round(num(s)))
  const m = Math.floor(s / 60), sec = s % 60
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
}

const fmtDay = (day) => fmtDayShort(day)

const shiftLabel = (t, code) => (code ? t(`production:shift.${code}`, { defaultValue: String(code) }) : '—')

function useChartTheme() {
  const { dark } = useTheme()
  return useMemo(() => ({
    grid: dark ? '#334155' : '#e2e8f0',
    tick: { fontSize: 11, fill: dark ? '#94a3b8' : '#64748b' },
    tooltip: {
      fontSize: 12,
      borderRadius: 8,
      border: `1px solid ${dark ? '#334155' : '#e2e8f0'}`,
      background: dark ? '#0f172a' : '#ffffff',
      color: dark ? '#e2e8f0' : '#334155',
    },
    legend: { fontSize: 12, color: dark ? '#cbd5e1' : '#475569' },
  }), [dark])
}

function Card({ title, right, children, className = '' }) {
  return (
    <div className={`bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-5 ${className}`}>
      {(title || right) && (
        <div className="flex items-center justify-between gap-3 mb-4">
          {title && <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{title}</h3>}
          {right}
        </div>
      )}
      {children}
    </div>
  )
}

function Sparkline({ data, dataKey, color }) {
  if (!Array.isArray(data) || data.length < 2) return <div className="h-9" />
  return (
    <div className="h-9">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 4, right: 2, bottom: 2, left: 2 }}>
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={1.5} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function Tile({ label, value, unit, spark, sparkKey, sparkColor = C_REAL }) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm px-4 pt-3 pb-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</p>
      <p className="text-xl font-bold tabular-nums text-slate-800 dark:text-slate-100 mt-0.5 leading-tight">
        {value}{unit && <span className="text-xs font-normal text-slate-400 dark:text-slate-500 ml-1">{unit}</span>}
      </p>
      <Sparkline data={spark} dataKey={sparkKey} color={sparkColor} />
    </div>
  )
}

// Indicador bullet: barra fina (valor) sobre trilha até 100%, marcador "meta".
// pct vem em pontos percentuais; só a LARGURA da barra é limitada a 100, o número
// mostra o valor real (UE pode passar de 100% quando o ciclo cruza a janela).
function Bullet({ label, pct, target }) {
  const { t } = useTranslation()
  const v = num(pct)
  const w = Math.max(0, Math.min(100, v))
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</span>
        <span className="text-sm font-bold tabular-nums text-slate-800 dark:text-slate-100">{fmtPct(v)}</span>
      </div>
      <div className="relative h-2.5 rounded-full bg-slate-100 dark:bg-slate-800">
        <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${w}%`, background: C_REAL }} />
        <div className="absolute -top-1 -bottom-1 w-0.5 rounded-full" style={{ left: `${target}%`, background: C_PLAN }} />
      </div>
      <p className="text-[10px] text-slate-400 dark:text-slate-500 mt-1 tabular-nums" style={{ marginLeft: `min(${target}%, 85%)` }}>
        {t('production:kpis.target_pct', { target })}
      </p>
    </div>
  )
}

// Modelo de tempo GMG: UMA barra horizontal 100% empilhada + legenda com valores.
function TimeModelBar({ gmg }) {
  const { t } = useTranslation()
  // byBucket vem em MINUTOS (o resto da resposta vem em segundos); aqui tudo é
  // comparado com productiveSecs, então ×60.
  const buckets = gmg?.stops?.byBucket ?? {}
  const productive = num(gmg?.productiveSecs)
  const delay = num(buckets.delay) * 60
  const standby = num(buckets.standby) * 60
  const nonProductive = num(buckets.non_productive) * 60
  const downtime = num(buckets.downtime) * 60
  const calendar = num(gmg?.calendarSecs)
  const parts = productive + delay + standby + nonProductive + downtime
  const idle = Math.max(0, calendar - parts)
  const segs = [
    { key: 'prod', label: t('production:gmg.productive'), secs: productive, color: G_PROD },
    { key: 'delay', label: t('production:gmg.delay'), secs: delay, color: G_DELAY },
    { key: 'standby', label: t('production:gmg.standby'), secs: standby, color: G_STANDBY },
    { key: 'nonprod', label: t('production:gmg.non_productive'), secs: nonProductive, color: G_NONPROD },
    { key: 'down', label: t('production:gmg.downtime'), secs: downtime, color: G_DOWN },
    { key: 'idle', label: t('production:gmg.idle'), secs: idle, color: G_IDLE },
  ]
  // A base é o tempo de calendário. Quando as partes somam mais que ele (paragem em
  // aberto, ciclo que cruza a janela), a barra passa a usar a soma e diz isso, em vez
  // de renormalizar em silêncio e apresentar percentuais de um total inflado.
  const over = parts > calendar + 1
  const total = Math.max(calendar, parts)
  if (total <= 0) return <p className="text-sm text-slate-400 dark:text-slate-500">{t('common:states.empty')}</p>

  return (
    <div>
      <div className="flex h-6 rounded-lg overflow-hidden" style={{ gap: 2 }}>
        {segs.filter(s => s.secs > 0).map(s => (
          <div key={s.key} className="h-full first:rounded-l-lg last:rounded-r-lg" title={`${s.label}: ${fmtHours(s.secs)}`}
            style={{ width: `${(s.secs / total) * 100}%`, background: s.color, minWidth: 3 }} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-3">
        {segs.map(s => (
          <div key={s.key} className="flex items-center gap-1.5 text-xs">
            <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: s.color }} />
            <span className="text-slate-600 dark:text-slate-300">{s.label}</span>
            <span className="tabular-nums text-slate-400 dark:text-slate-500">{fmtHours(s.secs)}</span>
            <span className="tabular-nums font-semibold text-slate-700 dark:text-slate-200">{total > 0 ? Math.round((s.secs / total) * 100) : 0}%</span>
          </div>
        ))}
      </div>
      {over && (
        <p className="mt-2 flex items-start gap-1.5 text-[11px] text-status-stop">
          <AlertTriangle size={12} className="shrink-0 mt-0.5" />
          {t('production:gmg.over_calendar', { calendar: fmtHours(calendar), parts: fmtHours(parts) })}
        </p>
      )}
    </div>
  )
}

function ParetoTooltip({ active, payload }) {
  const { t } = useTranslation()
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm px-3 py-2 text-xs">
      <p className="font-semibold text-slate-700 dark:text-slate-200 mb-1">{d.reason}</p>
      <p className="text-slate-500 dark:text-slate-400 tabular-nums">{fmtInt(d.minutes)} {t('common:units.minutes')} · {t('production:kpis.pareto_stops', { count: num(d.count) })}</p>
      <p className="text-slate-500 dark:text-slate-400 tabular-nums">{t('production:kpis.pareto_pct', { pct: fmt(d.pct), cum: fmt(d.cumPct) })}</p>
    </div>
  )
}

// Pareto de paradas: UM eixo percentual; barras = % dos minutos totais, linha = % acumulado.
function StopsPareto({ pareto }) {
  const { t } = useTranslation()
  const ct = useChartTheme()
  const data = useMemo(() => {
    const rows = (Array.isArray(pareto) ? pareto : [])
      .map(p => ({ reason: p.reason || '—', minutes: num(p.minutes), count: num(p.count) }))
      .sort((a, b) => b.minutes - a.minutes)
      .slice(0, 8)
    const total = rows.reduce((a, r) => a + r.minutes, 0)
    let cum = 0
    return rows.map(r => {
      const pct = total > 0 ? (r.minutes / total) * 100 : 0
      cum += pct
      return { ...r, pct, cumPct: Math.min(100, cum) }
    })
  }, [pareto])

  if (data.length === 0) return <p className="text-sm text-slate-400 dark:text-slate-500">{t('production:kpis.no_stops')}</p>

  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 4, left: -18 }}>
          <CartesianGrid stroke={ct.grid} vertical={false} />
          <XAxis dataKey="reason" tick={{ ...ct.tick, fontSize: 10 }} interval={0} angle={-22} textAnchor="end" height={56} tickLine={false} axisLine={{ stroke: ct.grid }} />
          <YAxis domain={[0, 100]} tick={ct.tick} tickFormatter={v => `${v}%`} tickLine={false} axisLine={false} />
          <Tooltip content={<ParetoTooltip />} />
          <Legend wrapperStyle={ct.legend} />
          <Bar name={t('production:kpis.pct_stopped_minutes')} dataKey="pct" fill={C_WASTE} radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false} />
          <Line name={t('production:kpis.pct_cumulative')} type="monotone" dataKey="cumPct" stroke={C_FOURTH} strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

function DailyChart({ byDay }) {
  const { t } = useTranslation()
  const ct = useChartTheme()
  const data = useMemo(() => (byDay ?? []).map(d => ({
    ...d, dayLabel: fmtDay(d.day), ton: num(d.ton), planTon: num(d.planTon),
  })), [byDay])
  if (data.length === 0) return <p className="text-sm text-slate-400 dark:text-slate-500">{t('common:states.empty')}</p>
  return (
    <div className="h-64">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -10 }} barGap={2}>
          <CartesianGrid stroke={ct.grid} vertical={false} />
          <XAxis dataKey="dayLabel" tick={ct.tick} tickLine={false} axisLine={{ stroke: ct.grid }} />
          <YAxis tick={ct.tick} tickFormatter={fmtInt} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={ct.tooltip} formatter={(v, name) => [`${fmtInt(v)} ${t('common:units.tons')}`, name]} />
          <Legend wrapperStyle={ct.legend} />
          <Bar name={t('production:kpis.actual_t')} dataKey="ton" fill={C_REAL} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
          <Bar name={t('production:kpis.plan_t')} dataKey="planTon" fill={C_PLAN} radius={[4, 4, 0, 0]} maxBarSize={28} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}

function CumChart({ byDay }) {
  const { t } = useTranslation()
  const ct = useChartTheme()
  const data = useMemo(() => (byDay ?? []).map(d => ({
    ...d, dayLabel: fmtDay(d.day), cumTon: num(d.cumTon), cumPlan: num(d.cumPlan),
  })), [byDay])
  if (data.length === 0) return null
  return (
    <div className="h-48">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -10 }}>
          <CartesianGrid stroke={ct.grid} vertical={false} />
          <XAxis dataKey="dayLabel" tick={ct.tick} tickLine={false} axisLine={{ stroke: ct.grid }} />
          <YAxis tick={ct.tick} tickFormatter={fmtInt} tickLine={false} axisLine={false} />
          <Tooltip contentStyle={ct.tooltip} formatter={(v, name) => [`${fmtInt(v)} ${t('common:units.tons')}`, name]} />
          <Legend wrapperStyle={ct.legend} />
          <Line name={t('production:kpis.cum_actual_t')} type="monotone" dataKey="cumTon" stroke={C_REAL} strokeWidth={2} dot={false} isAnimationActive={false} />
          <Line name={t('production:kpis.cum_plan_t')} type="monotone" dataKey="cumPlan" stroke={C_PLAN} strokeWidth={2} dot={false} isAnimationActive={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

function ShiftTable({ byShift }) {
  const { t } = useTranslation()
  const rows = Array.isArray(byShift) ? byShift : []
  if (rows.length === 0) return null
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <th className="px-3 py-2 font-semibold">{t('production:kpis.day')}</th>
            <th className="px-3 py-2 font-semibold">{t('common:entities.shift')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.actual_t')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.plan_t')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.attainment')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s, i) => {
            const plan = num(s.planTon)
            const att = plan > 0 ? (num(s.ton) / plan) * 100 : null
            const cls = att == null ? 'text-slate-400 dark:text-slate-500'
              : att >= 100 ? 'text-teal-600 dark:text-teal-400'
              : att < 70 ? 'text-red-600 dark:text-red-400'
              : 'text-slate-700 dark:text-slate-200'
            return (
              <tr key={`${s.day}-${s.shift}-${i}`} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800">
                <td className="px-3 py-2 text-slate-700 dark:text-slate-200 tabular-nums">{fmtDay(s.day)}</td>
                <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{shiftLabel(t, s.shift)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtInt(s.ton)}</td>
                {/* O plano é DIÁRIO: quando o backend o rateia pelos turnos do dia
                    (planTonProrated), o número não é uma meta cadastrada por turno e vai
                    marcado com ≈, senão o atingimento por turno lê como oficial. */}
                <td className="px-3 py-2 text-right tabular-nums text-slate-500 dark:text-slate-400"
                  title={plan > 0 && s.planTonProrated ? t('production:kpis.plan_prorated_hint') : undefined}>
                  {plan > 0 ? `${s.planTonProrated ? '≈ ' : ''}${fmtInt(plan)}` : '—'}
                </td>
                <td className={`px-3 py-2 text-right tabular-nums font-semibold ${cls}`}>
                  <span className="inline-flex items-center gap-1 justify-end">
                    {att != null && att < 70 && <TrendingDown size={13} className="shrink-0" />}
                    {att == null ? '—' : `${fmt(att)}%`}
                  </span>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// Lista de barras horizontais com rótulos diretos (top 5 por zona), teal.
function ZoneList({ rows }) {
  const { t } = useTranslation()
  const top = (Array.isArray(rows) ? rows : [])
    .map(z => ({ zone: z.zone || '—', ton: num(z.ton), cycles: num(z.cycles) }))
    .sort((a, b) => b.ton - a.ton)
    .slice(0, 5)
  if (top.length === 0) return <p className="text-sm text-slate-400 dark:text-slate-500">{t('common:states.empty')}</p>
  const max = Math.max(1, ...top.map(z => z.ton))
  return (
    <div className="space-y-2.5">
      {top.map(z => (
        <div key={z.zone} className="text-xs" title={`${z.zone}: ${fmtInt(z.ton)} ${t('common:units.tons')} · ${t('production:kpis.zone_cycles', { count: z.cycles })}`}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-slate-600 dark:text-slate-300 truncate">{z.zone}</span>
            <span className="tabular-nums text-slate-700 dark:text-slate-200 font-semibold shrink-0 ml-2">
              {fmtInt(z.ton)} {t('common:units.tons')} <span className="font-normal text-slate-400 dark:text-slate-500">· {t('production:kpis.zone_cycles', { count: z.cycles })}</span>
            </span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 dark:bg-slate-800">
            <div className="h-full rounded-full" style={{ width: `${(z.ton / max) * 100}%`, background: C_REAL }} />
          </div>
        </div>
      ))}
    </div>
  )
}

// Os seis baldes do contrato (kpis.go stepAvgs). Perfuração e suporte faltavam, então
// numa frota de perfuratrizes o cartão saía vazio com segundos de perfuração medidos.
const STEP_ORDER = [
  { key: 'queue', labelKey: 'production:kpis.step_queue', color: C_REAL },
  { key: 'load', labelKey: 'production:kpis.step_load', color: C_PLAN },
  { key: 'travel', labelKey: 'production:kpis.step_travel', color: C_WASTE },
  { key: 'unload', labelKey: 'production:kpis.step_unload', color: C_FOURTH },
  { key: 'drill', labelKey: 'production:kpis.step_drill', color: G_STANDBY },
  { key: 'support', labelKey: 'production:kpis.step_support', color: G_IDLE },
]

// Tempos médios por etapa do ciclo: barras horizontais em ordem fixa, rótulo mm:ss.
// Só entram os baldes com tempo medido (a frota mistura tipos e nem todo tipo tem
// todos os passos).
function StepBars({ stepAvgs }) {
  const { t } = useTranslation()
  const all = STEP_ORDER.map(s => ({ ...s, label: t(s.labelKey), secs: num(stepAvgs?.[s.key]) }))
  const steps = all.filter(s => s.secs > 0)
  const max = Math.max(1, ...steps.map(s => s.secs))
  const samples = num(stepAvgs?.samples)
  if (steps.length === 0) return <p className="text-sm text-slate-400 dark:text-slate-500">{t('common:states.empty')}</p>
  return (
    <div className="space-y-2.5">
      {steps.map(s => (
        <div key={s.key} className="flex items-center gap-2 text-xs" title={`${s.label}: ${fmtMMSS(s.secs)}`}>
          <span className="w-20 shrink-0 truncate text-slate-500 dark:text-slate-400">{s.label}</span>
          <div className="flex-1 h-3 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${(s.secs / max) * 100}%`, background: s.color }} />
          </div>
          <span className="w-12 shrink-0 text-right tabular-nums font-semibold text-slate-700 dark:text-slate-200">{fmtMMSS(s.secs)}</span>
        </div>
      ))}
      {/* A média por passo é sobre as amostras da janela, que incluem transições de estado
          dos tipos que não fecham ciclo: sem o n, uma média de duas amostras lê igual a uma
          de duzentas. */}
      {samples > 0 && (
        <p className="text-[11px] text-slate-400 dark:text-slate-500 pt-0.5">{t('production:kpis.step_samples', { count: samples })}</p>
      )}
    </div>
  )
}

function OperatorTable({ byOperator }) {
  const { t } = useTranslation()
  const rows = Array.isArray(byOperator) ? byOperator : []
  if (rows.length === 0) return null
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <th className="px-3 py-2 font-semibold">{t('common:entities.operator')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('common:entities.cycles')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('common:units.tons')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.avg_time')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o, i) => (
            <tr key={o.operator ?? i} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800">
              <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{o.operator || '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtInt(o.cycles)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtInt(o.ton)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtMMSS(o.avgSecs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DeviceGmgTable({ byDevice }) {
  const { t } = useTranslation()
  const rows = Array.isArray(byDevice) ? byDevice : []
  if (rows.length === 0) return null
  return (
    <div className="overflow-x-auto scrollbar-thin">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-wider text-slate-400 dark:text-slate-500 border-b border-slate-200 dark:border-slate-700">
            <th className="px-3 py-2 font-semibold">{t('common:entities.equipment')}</th>
            <th className="px-3 py-2 font-semibold">{t('production:kpis.type')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.df')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.uf')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.ro')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:gmg.downtime')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:gmg.standby')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:gmg.non_productive')}</th>
            <th className="px-3 py-2 font-semibold text-right">{t('production:kpis.worked')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((d, i) => (
            <tr key={d.device ?? i} className="border-b border-slate-100 dark:border-slate-800 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800">
              <td className="px-3 py-2 font-medium text-slate-700 dark:text-slate-200">{d.device || '—'}</td>
              <td className="px-3 py-2 text-slate-500 dark:text-slate-400">{d.type || '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtPct(d.df)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtPct(d.uf)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtPct(d.ro)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtHours(d.downtimeSecs)}</td>
              {/* standbySecs JÁ INCLUI o não produtivo (é o mesmo lado do tempo
                  disponível na conta de DF/UF); a coluna ao lado desdobra o número,
                  senão refeição e treinamento apareciam rotulados como "Espera" e
                  discordavam da barra do modelo de tempo logo acima, na mesma tela. */}
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtHours((d.standbySecs || 0) - (d.nonProductiveSecs || 0))}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtHours(d.nonProductiveSecs)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200">{fmtHours(d.workedSecs)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MiniStat({ label, value, hint }) {
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2" title={hint}>
      <p className="text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">{label}</p>
      <p className="text-base font-semibold text-slate-800 dark:text-slate-100 mt-0.5 tabular-nums leading-none">{value}</p>
    </div>
  )
}

function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 xl:grid-cols-7">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-24 rounded-xl bg-slate-100 dark:bg-slate-800" />
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="h-72 rounded-xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-72 rounded-xl bg-slate-100 dark:bg-slate-800" />
      </div>
      <div className="h-64 rounded-xl bg-slate-100 dark:bg-slate-800" />
    </div>
  )
}

export default function ProductivityView() {
  const { t, i18n: i18nHook } = useTranslation()
  const { mineQS } = useScope()
  const [range, setRange] = useState({ preset: '7d' })
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const query = useMemo(() => {
    const p = new URLSearchParams()
    if (range.preset) p.set('preset', range.preset)
    else { p.set('from', range.from); p.set('to', range.to) }
    const qs = p.toString()
    return mineQS ? `${qs}&${mineQS}` : qs
  }, [range, mineQS])

  // O AbortController evita que a resposta de um filtro antigo, mais lenta, vença a
  // do filtro atual (trocar o período duas vezes seguidas invertia os dados na tela).
  const abortRef = useRef(null)

  const load = useCallback(() => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true)
    setError(false)
    fetch(`/api/kpis?${query}`, { signal: ctrl.signal })
      .then(r => {
        if (!r.ok) throw new Error(`http ${r.status}`)
        return r.json()
      })
      .then(d => { if (!ctrl.signal.aborted) setData(d && typeof d === 'object' ? d : null) })
      .catch(err => { if (err.name !== 'AbortError') { setData(null); setError(true) } })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false) })
  }, [query])

  useEffect(() => {
    load()
    return () => abortRef.current?.abort()
  }, [load])

  const prod = data?.production ?? {}
  const gmg = data?.gmg ?? {}
  const cycles = data?.cycles ?? {}
  const drilling = data?.drilling ?? {}
  const byDay = Array.isArray(prod.byDay) ? prod.byDay : []

  const empty = !error && data != null &&
    byDay.length === 0 && num(prod.totalTon) === 0 && num(cycles.count) === 0 &&
    num(gmg.calendarSecs) === 0 && num(drilling.holes) === 0

  return (
    <div className="h-full flex flex-col">
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 px-6 py-3 flex items-center gap-4 flex-wrap">
        <DateRangePicker value={range} onChange={setRange} timezone={data?.window?.timezone || ''} />
        <ScopeChip />
        {/* O corte do dia e do turno é feito no fuso da operação, não no do navegador:
            sem dizer qual é, "hoje" com o operador noutro fuso lê como erro de conta. */}
        {data?.window?.timezone && (
          <span className="text-xs text-slate-400 dark:text-slate-500" title={t('production:kpis.timezone_hint')}>
            {data.window.timezone}
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <a href={`/api/report/pdf?${query}&lang=${encodeURIComponent(i18nHook.language)}`} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
            <Download size={14} /> {t('common:actions.export_pdf')}
          </a>
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> {t('common:actions.refresh')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 scrollbar-thin">
        {loading && !data ? (
          <Skeleton />
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
            <AlertTriangle size={40} className="mb-3 text-slate-300 dark:text-slate-600" />
            <p className="text-sm">{t('production:kpis.load_error')}</p>
            <button onClick={load}
              className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800">
              <RefreshCw size={14} /> {t('common:actions.retry')}
            </button>
          </div>
        ) : empty || !data ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400 dark:text-slate-500">
            <Activity size={40} className="mb-3 text-slate-300 dark:text-slate-600" />
            <p className="text-sm">{t('common:states.empty')}</p>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-4 xl:grid-cols-7">
              <Tile label={t('production:kpis.tons')} value={fmt(prod.totalTon)} unit={t('common:units.tons')} spark={byDay} sparkKey="ton" />
              <Tile label={t('common:units.bcm')} value={fmt(prod.totalBcm)} spark={byDay} sparkKey="bcm" />
              <Tile label={t('common:entities.cycles')} value={fmtInt(cycles.count ?? prod.cycles)} />
              <Tile label={t('production:kpis.df')} value={fmtPct(gmg.df)} />
              <Tile label={t('production:kpis.uf')} value={fmtPct(gmg.uf)} />
              <Tile label={t('production:kpis.ro')} value={fmtPct(gmg.ro)} />
              <Tile label={t('production:kpis.meters_drilled')} value={fmt(drilling.meters)} unit={t('common:units.meters')} />
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t('production:kpis.plan_vs_actual_daily')}>
                <DailyChart byDay={byDay} />
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  <MiniStat label={t('production:kpis.ore')} value={`${fmt(prod.oreTon)} ${t('common:units.tons')}`} />
                  <MiniStat label={t('production:kpis.waste')} value={`${fmt(prod.wasteTon)} ${t('common:units.tons')}`} />
                  <MiniStat label={t('production:kpis.pace')} value={`${fmt(prod.paceTonPerDay)} ${t('common:units.tons_per_day')}`} />
                  {/* A projeção é linear sobre a fração do período já decorrida; abaixo de
                      5% o backend devolve o próprio realizado, então o rótulo diz qual dos
                      dois números está na tela. */}
                  <MiniStat
                    label={num(prod.forecastFrac) < 0.05 ? t('production:kpis.realized') : t('production:kpis.forecast')}
                    value={`${fmt(prod.forecastTon)} ${t('common:units.tons')}`}
                    hint={t('production:kpis.forecast_hint', { pct: fmt(num(prod.forecastFrac) * 100) })} />
                </div>
              </Card>
              <Card title={t('production:kpis.cumulative')}>
                <CumChart byDay={byDay} />
                <div className="mt-4">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{t('production:kpis.by_shift')}</p>
                  <ShiftTable byShift={prod.byShift} />
                </div>
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t('production:kpis.load_zones_top')}><ZoneList rows={prod.byLoadZone} /></Card>
              <Card title={t('production:kpis.dump_zones_top')}><ZoneList rows={prod.byDumpZone} /></Card>
            </div>

            <Card title={t('production:kpis.gmg_title')}>
              <div className="grid gap-6 sm:grid-cols-3 mb-6">
                <Bullet label={t('production:kpis.df_full')} pct={gmg.df} target={TARGETS.df} />
                <Bullet label={t('production:kpis.uf_full')} pct={gmg.uf} target={TARGETS.uf} />
                <Bullet label={t('production:kpis.ro_full')} pct={gmg.ro} target={TARGETS.ro} />
              </div>
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{t('production:kpis.time_model')}</p>
              <TimeModelBar gmg={gmg} />
              {/* UE tem duas bases e a API devolve as duas: sobre o tempo TRABALHADO (a
                  definição da API) e sobre o calendário. Cada uma vai com o nome do que é,
                  senão o mesmo indicador aparece com dois valores sem explicação. */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-5">
                <MiniStat label={t('production:kpis.ue')} value={fmtPct(gmg.ue)} hint={t('production:kpis.ue_hint')} />
                <MiniStat label={t('production:kpis.ue_calendar')} value={fmtPct(gmg.ueCalendar)} hint={t('production:kpis.ue_calendar_hint')} />
                <MiniStat label={t('common:entities.stops')} value={fmtInt(gmg.stops?.count)} />
                <MiniStat label={t('production:kpis.breakdowns')} value={fmtInt(gmg.breakdowns)} />
                <MiniStat label={t('production:kpis.avg_stop_time')} value={`${fmt(gmg.stops?.avgMinutes)} ${t('common:units.minutes')}`} />
                <MiniStat label={t('production:kpis.mtbf')} value={`${fmt(gmg.mtbfHours)} ${t('common:units.hours')}`} />
                <MiniStat label={t('production:kpis.mttr')} value={`${fmt(gmg.mttrHours)} ${t('common:units.hours')}`} />
              </div>
            </Card>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t('production:kpis.stops_pareto')}>
                <StopsPareto pareto={gmg.stops?.pareto} />
              </Card>
              <Card title={t('production:kpis.gmg_by_device')}>
                <DeviceGmgTable byDevice={gmg.byDevice} />
                {(!gmg.byDevice || gmg.byDevice.length === 0) && (
                  <p className="text-sm text-slate-400 dark:text-slate-500">{t('common:states.empty')}</p>
                )}
              </Card>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <Card title={t('production:kpis.cycle_times')}>
                <div className="grid grid-cols-3 gap-2 mb-5">
                  <MiniStat label={t('common:entities.cycles')} value={fmtInt(cycles.count)} />
                  <MiniStat label={t('production:kpis.avg_cycle')} value={fmtMMSS(cycles.avgSecs)} />
                  <MiniStat label={t('production:kpis.cycles_per_hour')} value={fmt(cycles.perHour)} />
                </div>
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">{t('production:kpis.avg_time_per_step')}</p>
                <StepBars stepAvgs={cycles.stepAvgs} />
              </Card>
              <Card title={t('production:kpis.cycles_by_operator')}>
                <OperatorTable byOperator={cycles.byOperator} />
                {(!cycles.byOperator || cycles.byOperator.length === 0) && (
                  <p className="text-sm text-slate-400 dark:text-slate-500">{t('common:states.empty')}</p>
                )}
              </Card>
            </div>

            <Card title={t('production:kpis.drilling')}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <MiniStat label={t('common:entities.holes')} value={fmtInt(drilling.holes)} />
                <MiniStat label={t('production:kpis.meters')} value={`${fmt(drilling.meters)} ${t('common:units.meters')}`} />
                <MiniStat label={t('production:kpis.meters_per_hour')} value={`${fmt(drilling.metersPerHour)} ${t('common:units.meters_per_hour')}`} />
                <MiniStat label={t('production:kpis.avg_per_hole')} value={`${fmt(drilling.avgDepthPerHole)} ${t('common:units.meters')}`} />
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
